import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { Input } from 'components/input'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { Divider } from 'components/divider'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_STATION_REVENUE_REPORT, SEARCH_LOCATION, GET_ROUTE_TYPES, GET_ROUTES_FOR_REPORTS } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import i18n from 'i18next'
import { RoutesOperationsIcon } from 'components/icons'

const INITIAL_FILTER_STATE = {
    travel_date: {
        start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    station_ids: [],
    route_ids: [],
    trip_ids: [],
    route_type: null,
}

export default function StationRevenueReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    // States
    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [locations, setLocations] = useState([])
    const [routes, setRoutes] = useState([])
    const [trips, setTrips] = useState([])
    const [routeTypes, setRouteTypes] = useState([])
    const [filterApplied, setFilterApplied] = useState(false)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
        }
    }, [])

    // GraphQL query
    const [getStationRevenueReport, { loading: reportLoading }] = useLazyQuery(GET_STATION_REVENUE_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.stationAggregatedRevenueReport.success && data.stationAggregatedRevenueReport.code === '200') {
                setData(data.stationAggregatedRevenueReport.station_revenue_report.map(formatRevenueData))
            } else {
                handleError(data.stationAggregatedRevenueReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    // Add search location query
    const [searchLocation] = useLazyQuery(SEARCH_LOCATION, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.searchLocation.success && data.searchLocation.code === '200') {
                setLocations(data.searchLocation.locations)
            }
        },
        onError: error => handleError(error.message),
    })

    // Get route types query
    const [getRouteTypes] = useLazyQuery(GET_ROUTE_TYPES, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.routeType) {
                setRouteTypes(data.routeType)
            }
        },
        onError: error => handleError(error.message),
    })

    // Get routes query
    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getBoatRoute.success && data.getBoatRoute.code === '200') {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
        onError: error => handleError(error.message),
    })

    // Helper Functions
    const formatRevenueData = station => ({
        serial_number: station.serial_number,
        station_name: station.station_name?.en ? station.station_name?.en : station.station_name?.ar,
        total_bookings: station.total_bookings,
        total_passengers: station.total_passengers,
        infant_count: station.infant_count,
        child_count: station.child_count,
        adult_count: station.adult_count,
        senior_count: station.senior_count,
        gross_revenue: station.gross_revenue,
        wallet_spend: station.wallet_spend,
        wallet_refunds: station.wallet_refunds,
        total_discounts: station.total_discounts,
        net_revenue: station.net_revenue,
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch station revenue report'))
        setShowErrDialog(true)
    }

    // Export function
    const [getStationRevenueReportForExport] = useLazyQuery(GET_STATION_REVENUE_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    const fetchAllData = async (filterData = filterObj) => {
        try {
            const result = await getStationRevenueReportForExport({
                variables: {
                    page: {}, // no pagination
                    stationFilter: {
                        travel_date: {
                            start_date:
                                filterData.travel_date.start_date ||
                                moment().subtract(1, 'months').format('YYYY-MM-DD'),
                            end_date: filterData.travel_date.end_date || moment().format('YYYY-MM-DD'),
                        },
                        station_ids: filterData.station_ids?.length > 0 ? filterData.station_ids : null,
                        route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                        trip_ids: filterData.trip_ids?.length > 0 ? filterData.trip_ids : null,
                        route_type: filterData.route_type || null,
                    },
                },
            })

            if (
                result.data?.stationAggregatedRevenueReport?.success &&
                result.data.stationAggregatedRevenueReport.code === '200'
            ) {
                return {
                    data: result.data.stationAggregatedRevenueReport.station_revenue_report,
                }
            } else {
                throw new Error(result.data?.stationAggregatedRevenueReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)
            const currentFilterObj = { ...filterObj }
            const allResult = await fetchAllData(currentFilterObj)
            const allData = allResult.data.map(formatRevenueData)

            if (allData.length === 0) {
                handleError(t('No records found to export'))
                return
            }

            const headers = [
                '#',
                'Station Name',
                'Total Bookings',
                'Total Passengers',
                'Infant',
                'Child',
                'Adult',
                'Senior',
                'Gross Revenue (BHD)',
                'Wallet Spent (BHD)',
                'Wallet Refunds (BHD)',
                'Total Discounts (BHD)',
                'Net Revenue (BHD)',
            ]

            const csvContent = [
                headers.join(','),
                ...allData.map(row =>
                    [
                        row.serial_number,
                        row.station_name,
                        row.total_bookings,
                        row.total_passengers,
                        row.infant_count ?? 0,
                        row.child_count ?? 0,
                        row.adult_count ?? 0,
                        row.senior_count ?? 0,
                        (row.gross_revenue ?? 0).toFixed(3),
                        (row.wallet_spend ?? 0).toFixed(3),
                        (row.wallet_refunds ?? 0).toFixed(3),
                        (row.total_discounts ?? 0).toFixed(3),
                        (row.net_revenue ?? 0).toFixed(3),
                    ].join(',')
                ),
            ].join('\n')

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `station-revenue-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    // Table columns configuration
    const columns = useMemo(
        () => [
            {
                name: t('#'),
                selector: row => row.serial_number,
                width: '70px',
                sortable: false,
                minWidth: '70px',
                wrap: true,
            },
            {
                name: t('Station Name'),
                selector: row => row.station_name,
                sortable: true,
                minWidth: '150px',
                wrap: true,
            },
            {
                name: t('Total Bookings'),
                selector: row => row.total_bookings,
                sortable: true,
                cell: row => <span className="font-medium">{row.total_bookings}</span>,
                minWidth: '120px',
                wrap: true,
            },
            {
                name: t('Total Passengers'),
                selector: row => row.total_passengers,
                sortable: true,
                cell: row => <span className="font-medium">{row.total_passengers}</span>,
                minWidth: '130px',
                wrap: true,
            },
            {
                name: t('Infant'),
                selector: row => row.infant_count,
                sortable: true,
                cell: row => <span className="font-medium">{row.infant_count ?? 0}</span>,
                minWidth: '80px',
                wrap: true,
            },
            {
                name: t('Child'),
                selector: row => row.child_count,
                sortable: true,
                cell: row => <span className="font-medium">{row.child_count ?? 0}</span>,
                minWidth: '80px',
                wrap: true,
            },
            {
                name: t('Adult'),
                selector: row => row.adult_count,
                sortable: true,
                cell: row => <span className="font-medium">{row.adult_count ?? 0}</span>,
                minWidth: '80px',
                wrap: true,
            },
            {
                name: t('Senior'),
                selector: row => row.senior_count,
                sortable: true,
                cell: row => <span className="font-medium">{row.senior_count ?? 0}</span>,
                minWidth: '80px',
                wrap: true,
            },
            {
                name: t('Gross Revenue (BHD)'),
                selector: row => row.gross_revenue,
                sortable: true,
                cell: row => (
                    <span>
                        <span className="font-bold">{(row.gross_revenue ?? 0).toFixed(3)}</span>
                    </span>
                ),
                minWidth: '160px',
                wrap: true,
            },
            {
                name: t('Wallet Spent (BHD)'),
                selector: row => row.wallet_spend,
                sortable: true,
                cell: row => (
                    <span>
                        <span className="font-bold">{(row.wallet_spend ?? 0).toFixed(3)}</span>
                    </span>
                ),
                minWidth: '160px',
                wrap: true,
            },
            {
                name: t('Wallet Refunds (BHD)'),
                selector: row => row.wallet_refunds,
                sortable: true,
                cell: row => (
                    <span>
                        <span className="font-bold">{(row.wallet_refunds ?? 0).toFixed(3)}</span>
                    </span>
                ),
                minWidth: '170px',
                wrap: true,
            },
            {
                name: t('Total Discounts (BHD)'),
                selector: row => row.total_discounts,
                sortable: true,
                cell: row => (
                    <span>
                        <span className="font-bold">{(row.total_discounts ?? 0).toFixed(3)}</span>
                    </span>
                ),
                minWidth: '170px',
                wrap: true,
            },
            {
                name: t('Net Revenue (BHD)'),
                selector: row => row.net_revenue,
                sortable: true,
                cell: row => (
                    <span>
                        <span className="font-bold">{(row.net_revenue ?? 0).toFixed(3)}</span>
                    </span>
                ),
                minWidth: '170px',
                wrap: true,
            },
        ],
        [t]
    )

    // Fetch report function (no pagination)
    const fetchStationRevenueReport = (filterData = filterObj) => {
        getStationRevenueReport({
            variables: {
                page: {}, // no pagination
                stationFilter: {
                    travel_date: {
                        start_date:
                            filterData.travel_date.start_date || moment().subtract(1, 'months').format('YYYY-MM-DD'),
                        end_date: filterData.travel_date.end_date || moment().format('YYYY-MM-DD'),
                    },
                    station_ids: filterData.station_ids?.length > 0 ? filterData.station_ids : null,
                    route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                    trip_ids: filterData.trip_ids?.length > 0 ? filterData.trip_ids : null,
                    route_type: filterData.route_type || null,
                },
            },
        })
    }

    // Effect to fetch data when filter is applied
    useEffect(() => {
        if (filterApplied) {
            fetchStationRevenueReport(filterObj)
        }
    }, [filterApplied])

    // Filter handlers
    const handleFilter = () => {
        setFilterApplied(true)
        fetchStationRevenueReport(filterObj)
    }

    const handleClear = () => {
        const resetFilter = {
            travel_date: {
                start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
            },
            station_ids: [],
            route_ids: [],
            trip_ids: [],
            route_type: null,
        }
        setFilterObj(resetFilter)
        setFilterApplied(false)
        setData([]) // Clear existing data
        setTrips([]) // Clear trips when clearing filters
    }

    // Custom table styles with auto width and RTL support
    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto', // Let columns determine their width
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto', // Let columns determine their width
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18n.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18n.language === 'ar' ? '0.5rem' : '0',
            },
        },
        noData: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
        },
    }

    // Update loading state to use GraphQL loading
    const isLoading = reportLoading

    // Add function to fetch locations
    const fetchLocations = (searchString = '') => {
        searchLocation({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
                searchString,
            },
        })
    }

    // Load locations and route types on component mount
    useEffect(() => {
        fetchLocations()
        getRouteTypes()
        fetchRoutes()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    // Fetch routes
    const fetchRoutes = () => {
        getRoutes({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
                statusCode: 'ACT',
            },
        })
    }

    // When routes are selected, collect trips from all selected routes
    useEffect(() => {
        if (filterObj.route_ids?.length > 0) {
            const selectedRoutes = routes.filter(r => filterObj.route_ids.includes(r.route_id))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            // Remove duplicates based on trip_id
            const uniqueTrips = allTrips.filter((trip, index, self) =>
                index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
        }
    }, [filterObj.route_ids, routes])

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <RoutesOperationsIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Station Earnings')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Revenue breakdown by station')}
                            </p>
                        </div>
                    </h1>

                    {/* Filters */}
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 ">{t('Travel Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.travel_date.start_date, filterObj.travel_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        ...filterObj,
                                        travel_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Station')}</label>
                            <MultiSelect
                                placeholder={t('All Stations')}
                                value={filterObj.station_ids || []}
                                onChange={(values) =>
                                    setFilterObj({
                                        ...filterObj,
                                        station_ids: values,
                                    })
                                }
                                options={locations.map(location => ({
                                    value: location.id,
                                    label: `${location.name.en}/${location.name.ar}`,
                                }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Route Type')}</label>
                            <Select
                                value={filterObj.route_type || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        route_type: e.target.value || null,
                                        station_ids: [], // Clear stations when route type changes
                                        route_ids: [], // Clear routes when route type changes
                                        trip_ids: [], // Clear trips when route type changes
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {routeTypes.map(type => (
                                    <option key={type.code} value={type.code}>
                                        {i18n.language === 'ar' ? type.description.ar : type.description.en}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Route')}</label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.route_ids || []}
                                onChange={(values) =>
                                    setFilterObj({
                                        ...filterObj,
                                        route_ids: values,
                                        trip_ids: [], // Clear trips when routes change
                                    })
                                }
                                options={routes
                                    .filter(route => {
                                        if (!filterObj.route_type) return true
                                        return route.master_route?.route_type?.code === filterObj.route_type
                                    })
                                    .map(route => ({
                                        value: route.route_id,
                                        label: i18n.language === 'ar' ? route.route_name.ar : route.route_name.en,
                                    }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Trip')}</label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.trip_ids || []}
                                onChange={(values) =>
                                    setFilterObj({
                                        ...filterObj,
                                        trip_ids: values,
                                    })
                                }
                                options={trips.map(trip => ({
                                    value: trip.trip_id,
                                    label: i18n.language === 'ar' ? trip.trip_name.ar : trip.trip_name.en,
                                }))}
                                disabled={filterObj.route_ids?.length === 0}
                            />
                        </div>

                        {/* Add filter buttons to grid */}
                        <div className="sm:col-span-1 flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {isLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">Station Revenue Reports</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <DataTable
                        columns={columns}
                        data={data}
                        pagination={false}
                        customStyles={customStyles}
                        noDataComponent={
                            <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="mx-auto h-12 w-12 text-gray-400"
                                >
                                    <path
                                        d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                        strokeWidth={2}
                                        vectorEffect="non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('Please add or modify your search criteria and try again.')}
                                </p>
                                <div className="mt-6"></div>
                            </div>
                        }
                    />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
